/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Drawer, Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import { LoadingSpinner } from './components/loadingSpinner';
import * as React from 'react';

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore,
    IEmailDelivery,
    IInvoicePaymentSummary
} from './index';
import { useCheckoutDatalayer } from '../../integrations/datalayer/hooks/useCheckoutDatalayer';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

export const LineItemsAccordion: React.FC<ILineItems> = lineItems => {
    const [accordionShouldBeOpen] = React.useState(() => {
        const shipCount = lineItems.itemsForShip?.lineItems.length ?? 0;
        const pickupCount = lineItems.itemsForPickup?.lineItems.length ?? 0;
        const emailCount = lineItems.itemsForEmail?.lineItems.length ?? 0;

        return !(shipCount > 3 || pickupCount > 3 || emailCount > 3);
    });

    return (
        <Drawer
            className='cart-line-items-accordion'
            toggleButtonText={lineItems.orderItemsHeading?.text || 'Order items'}
            ariaLabel={lineItems.orderItemsHeading?.text || 'Order items'}
            collapseProps={{ isOpen: accordionShouldBeOpen }}
        >
            <LineItemsComponent {...lineItems} />
        </Drawer>
    );
};

const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => (
    <div className='msc-order-summary-wrapper'>
        {heading}
        <div className='msc-order-summary__items'>
            {lines && (
                <>
                    {lines.subtotal}
                    {lines.shipping}
                    {lines.otherCharge}
                    {lines.tax}
                    {lines.totalDiscounts}
                    {lines.loyalty}
                    {lines.customerAccount}
                    {lines.giftCard}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps> = props => {
    const checkoutResult = props.data.checkout?.result;
    const cartLines = checkoutResult?.checkoutCart?.cart?.CartLines ?? undefined;
    const products = props.data.products.result ?? undefined;
    const totalAmount = checkoutResult?.checkoutCart?.cart?.SubtotalAmount ?? undefined;

    useCheckoutDatalayer({
        request: props.context.request,
        telemetry: props.telemetry,
        isPaymentVerificationRedirection: checkoutResult?.isPaymentVerificationRedirection ?? undefined,
        cartLines,
        products,
        totalAmount,
        cartId: checkoutResult?.checkoutCart?.cart?.Id ?? undefined,
        guestEmail: checkoutResult?.guestCheckoutEmail ?? undefined,
        shippingAddress: checkoutResult?.shippingAddress ?? undefined,
        billingAddress: checkoutResult?.billingAddress ?? undefined
    });

    const {
        config: { showAvailableRewards, showItemsInAccordion },
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        isPlaceOrderLoading,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        checkoutExpressPaymentContainer,
        checkoutErrorRef,
        loginLinkProps,
        loginLinkText,
        showLoginLink,
        promocodeProps,
        promoCode
    } = props;

    return (
        <>
            {isPlaceOrderLoading || loading ? <LoadingSpinner overlay={true} /> : null}
            <Module {...checkoutProps} ref={checkoutErrorRef}>
                {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                {!hasSalesOrder &&
                    isPaymentVerificationRedirection &&
                    shouldEnableSinglePaymentAuthorizationCheckout &&
                    !alert &&
                    !loading && <Waiting className='msc-waiting-circular msc-waiting-lg' />}
                {!hasSalesOrder && (
                    <Node {...bodyProps}>
                        {loading}
                        {alert}
                        {canShow && (
                            <>
                                <Node {...mainProps}>
                                    {checkoutExpressPaymentContainer}
                                    {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                    {showLoginLink &&
                                        (!props.context.request.user?.isAuthenticated ? (
                                            <Node {...loginLinkProps}>{loginLinkText}</Node>
                                        ) : null)}
                                    {guidedForm}
                                </Node>
                                <Node {...sideProps}>
                                    {showAvailableRewards && <Node {...promocodeProps}>{promoCode}</Node>}
                                    {!hasInvoiceLine
                                        ? orderSummary && <OrderSummaryComponent {...orderSummary} />
                                        : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                    <Node {...sideControlFirstProps}>
                                        <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                        {placeOrderButton}
                                        {keepShoppingButton}
                                    </Node>
                                    {lineItems && lineItems.LineItems && showItemsInAccordion ? (
                                        <LineItemsAccordion {...lineItems} />
                                    ) : lineItems && lineItems.LineItems ? (
                                        <LineItemsComponent {...lineItems} />
                                    ) : null}
                                    <Node {...sideControlSecondProps}>
                                        <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                        {placeOrderButton}
                                        {keepShoppingButton}
                                    </Node>
                                </Node>
                            </>
                        )}
                    </Node>
                )}
                {hasSalesOrder && orderConfirmation}
            </Module>
        </>
    );
};

export default CheckoutView;
